import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import ProfessionalHeader from '../components/ProfessionalHeader';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// X6 (U6): the dashboard as a workbench, not a progress bar. Replaces the old
// four stat cards / progress bar / "rate 10 drivers, 10 irons, 10 wedges"
// instructions -- that workflow no longer matches what the app does; the real
// workflow is Fitter's Corner (X1-X5). Backed by GET /expert/dashboard,
// which derives everything from the four bench tables since no `sessions`
// table exists (see that endpoint's docstring in expert.py).

interface BenchSession {
  profile_id: number;
  category: string;
  done: boolean;
  resume_pass: 'cut' | 'prescribe' | 'score' | 'build' | 'reveal';
  first_activity_at: string;
  last_activity_at: string;
  duration_minutes: number;
}

interface DashboardData {
  category: string;
  resume: BenchSession | null;
  last_sessions: BenchSession[];
  agreement: { rate: number | null; n_shared_scores: number; note: string | null };
  median_time_per_profile: { value_minutes: number | null; n_profiles: number; note: string };
}

const PASS_LABEL: Record<BenchSession['resume_pass'], string> = {
  cut: '01 Cut',
  prescribe: '02 Prescribe',
  score: '03 Score',
  build: '04 Build',
  reveal: '05 Reveal',
};

function timeAgo(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const mins = Math.max(0, Math.round(ms / 60000));
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

const ExpertDashboard: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const authHeaders = () => {
    const storageType = localStorage.getItem('auth_storage_type');
    const storage = storageType === 'session' ? sessionStorage : localStorage;
    const token = storage.getItem('access_token');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/v1/expert/dashboard`, {
        headers: authHeaders()
      });
      setData(response.data);
      setError(null);
    } catch (err) {
      // Never substitute placeholder numbers here. A fitter cannot tell invented
      // counts from real ones, and the fallback hid the outage that produced them.
      console.error('Error fetching expert dashboard:', err);
      setData(null);
      setError(
        axios.isAxiosError(err) && err.response
          ? `The server returned ${err.response.status} loading your dashboard.`
          : 'Could not reach the server to load your dashboard.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleStartNextGolfer = () => {
    navigate('/expert/bench');
  };

  const handleResume = () => {
    if (!data?.resume) return;
    navigate(`/expert/bench?profile_id=${data.resume.profile_id}&pass=${data.resume.resume_pass}&category=${data.resume.category}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <ProfessionalHeader user={user} onLogout={logout} />
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading your dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-gray-50">
        <ProfessionalHeader user={user} onLogout={logout} />
        <div className="max-w-2xl mx-auto px-4 py-16">
          <div className="bg-white border border-red-200 rounded-xl shadow-md p-8">
            <h2 className="text-xl font-semibold text-red-700 mb-2">
              Dashboard unavailable
            </h2>
            <p className="text-gray-700 mb-6">
              {error || 'No dashboard data was returned.'} Your existing ratings are unaffected.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => { setLoading(true); fetchDashboard(); }}
                className="px-5 py-2.5 rounded-lg font-semibold bg-blue-600 text-white hover:bg-blue-700"
              >
                Retry
              </button>
              <button
                onClick={handleStartNextGolfer}
                className="px-5 py-2.5 rounded-lg font-semibold border border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                Open Fitter's Corner anyway
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <ProfessionalHeader user={user} onLogout={logout} />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Resume in progress */}
        {data.resume ? (
          <div className="bg-white rounded-xl shadow-md p-6 mb-6 border-l-4 border-amber-500 flex items-center justify-between flex-wrap gap-4">
            <div>
              <div className="text-sm text-gray-500 uppercase tracking-wide">Resume in progress</div>
              <div className="text-xl font-semibold text-gray-900 mt-1">
                Profile #{data.resume.profile_id}
                <span className="text-base font-normal text-gray-500"> &middot; {data.resume.category}</span>
              </div>
              <div className="text-sm text-gray-600 mt-1">
                Left at {PASS_LABEL[data.resume.resume_pass]} &middot; last touched {timeAgo(data.resume.last_activity_at)}
              </div>
            </div>
            <button
              onClick={handleResume}
              className="px-6 py-3 rounded-lg font-semibold bg-amber-500 text-white hover:bg-amber-600"
            >
              Resume &rarr;
            </button>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-md p-6 mb-6 border-l-4 border-gray-300">
            <div className="text-sm text-gray-500 uppercase tracking-wide">Resume in progress</div>
            <div className="text-gray-700 mt-1">Nothing in progress right now.</div>
          </div>
        )}

        {/* Primary action */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-700 rounded-xl shadow-xl p-8 text-white mb-8 flex items-center justify-between flex-wrap gap-4">
          <div>
            <h2 className="text-2xl font-bold mb-1">Start next golfer</h2>
            <p className="text-blue-100">Cut, prescribe, score, and build a fit at Fitter's Corner.</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => navigate('/expert/explore')}
              className="px-6 py-4 rounded-lg font-semibold border-2 border-white/60 text-white hover:bg-white/10"
            >
              Explore the catalog
            </button>
            <button
              onClick={handleStartNextGolfer}
              className="px-8 py-4 rounded-lg font-bold text-lg shadow-lg transform transition-all hover:scale-105 bg-white text-blue-600 hover:bg-blue-50"
            >
              Start next golfer &rarr;
            </button>
          </div>
        </div>

        {/* Three numbers: last sessions, agreement, median time */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">

          {/* Agreement rate */}
          <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-purple-500">
            <div className="text-sm text-gray-500 uppercase tracking-wide">Agreement with other fitters</div>
            {data.agreement.rate !== null ? (
              <>
                <div className="text-4xl font-bold text-purple-600 mt-2">
                  {Math.round(data.agreement.rate * 100)}%
                </div>
                <div className="text-sm text-gray-600 mt-1">
                  {data.agreement.n_shared_scores} scores shared with other fitters
                </div>
              </>
            ) : (
              <div className="text-sm text-gray-500 mt-3">
                {data.agreement.note}
                {data.agreement.n_shared_scores > 0 && (
                  <span className="block mt-1 text-xs text-gray-400">
                    {data.agreement.n_shared_scores} shared score(s) so far
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Median time per profile */}
          <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-blue-500">
            <div className="text-sm text-gray-500 uppercase tracking-wide">Median time per profile</div>
            {data.median_time_per_profile.value_minutes !== null ? (
              <>
                <div className="text-4xl font-bold text-blue-600 mt-2">
                  {data.median_time_per_profile.value_minutes}m
                </div>
                <div className="text-sm text-gray-600 mt-1">
                  across {data.median_time_per_profile.n_profiles} completed profile
                  {data.median_time_per_profile.n_profiles === 1 ? '' : 's'}
                </div>
                <div className="text-xs text-gray-400 mt-2">{data.median_time_per_profile.note}</div>
              </>
            ) : (
              <div className="text-sm text-gray-500 mt-3">{data.median_time_per_profile.note}</div>
            )}
          </div>

          {/* Session count */}
          <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-green-ink">
            <div className="text-sm text-gray-500 uppercase tracking-wide">Profiles touched</div>
            <div className="text-4xl font-bold text-green-ink mt-2">{data.last_sessions.length}</div>
            <div className="text-sm text-gray-600 mt-1">shown below, most recent first</div>
          </div>
        </div>

        {/* Last 5 sessions */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Your last sessions</h2>
          {data.last_sessions.length === 0 ? (
            <p className="text-gray-500">No Fitter's Corner activity yet -- start your first golfer above.</p>
          ) : (
            <div className="divide-y divide-gray-100">
              {data.last_sessions.map(s => (
                <div key={s.profile_id} className="flex items-center justify-between py-3 flex-wrap gap-2">
                  <div>
                    <span className="font-medium text-gray-900">Profile #{s.profile_id}</span>
                    <span className="text-sm text-gray-500"> &middot; {s.category}</span>
                    <span className={`ml-3 text-xs font-semibold px-2 py-0.5 rounded-full ${
                      s.done ? 'bg-green/15 text-green-ink' : 'bg-amber-100 text-amber-700'
                    }`}>
                      {s.done ? 'done' : PASS_LABEL[s.resume_pass]}
                    </span>
                  </div>
                  <div className="text-sm text-gray-500">
                    {timeAgo(s.last_activity_at)} &middot; {s.duration_minutes}m
                    {!s.done && (
                      <button
                        onClick={() => navigate(`/expert/bench?profile_id=${s.profile_id}&pass=${s.resume_pass}&category=${s.category}`)}
                        className="ml-3 text-blue-600 hover:underline font-medium"
                      >
                        Resume
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExpertDashboard;
